/*
 * Copyright (c) 2020 Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { string, object, boolean, array } from "yup"; // 'number' is not used yet

// @material-ui/core
import withStyles from "@material-ui/core/styles/withStyles";
import createStyles from "@material-ui/core/styles/createStyles";
import {
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  TextField,
  IconButton,
  Checkbox,
  Radio,
  LinearProgress,
  Fab,
  Tooltip,
} from "@material-ui/core";

// @material-ui/icons
import {
  Undo,
  Close,
  ThumbUp,
  Visibility,
  VisibilityOff,
  HourglassEmptyOutlined,
} from "@material-ui/icons";

// TC UX
import Button from "components/CustomButtons/Button.jsx";

import { inputErrorStyle } from "assets/jss/views/accountPage/accountPageStyle";

import {
  sRadioErr,
  sPWRequired,
  sPWRule,
  sEmailRequired,
  sNameRequired,
  sMaxName,
  sMinName,
  sCodeRequired,
  sAgreRequired,
  sJobsRequired,
  sHowfRequired,
} from "assets/jss/views/ResStrings";

export const FormTextField = ({ field, form, ...rest }: any) => {
  const { type, icon, eye, label } = rest;
  const id: string = `form-id-${label}`;

  return (
    <div style={{ marginBottom: "0.4rem", position: "relative" }}>
      <div style={{ float: "left", position: "absolute", top: "1.2rem" }}>
        {icon}
      </div>
      <div style={{ marginLeft: "1.8rem" }}>
        <TextField
          fullWidth
          {...field}
          {...rest}
          id={id}
          type={type}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">{eye ? eye : ""}</InputAdornment>
            ),
          }}
        />
      </div>
      {form.errors[field.name] && form.touched[field.name] && (
        <span style={inputErrorStyle}>{form.errors[field.name]}</span>
      )}
    </div>
  );
};

export const FormMultilineField = ({ field, form, ...rest }: any) => {
  const { boxclass, limit, id } = rest;

  return (
    <TextField
      InputProps={{ disableUnderline: true, margin: "dense" }}
      inputProps={{ className: boxclass }}
      fullWidth
      {...field}
      {...rest}
      id={id}
      type="text"
      multiline={true}
      onInput={(e) => {
        e.target.value = e.target.value.substr(0, limit);
      }}
    />
  );
};

// styling the label of checkbox & radio button
//
const labelStyleNormal = (theme: any) => {
  return {
    root: { height: "36px" },
    label: {
      color: theme.palette.info.main,
      fontSize: "1.2rem",
      fontWeight: "300",
    },
  };
};

const labelStyleLarge = (theme: any) => {
  return {
    root: {
      // where there're multiple lines in the lablel, align chkbox and label at top
      alignItems: "flex-start",
    },
    label: {
      color: theme.palette.info.main,
      fontSize: "1.2rem",
      fontWeight: "300",
      marginTop: "6px",
    },
  };
};

// Checkbox - Single (for Field)
//
export const FormCheckboxField = ({ field, form, ...rest }: any) => {
  const { label } = rest;
  return (
    <>
      <FormControlLabel
        style={{ marginRight: 0 }}
        {...field}
        control={
          <Checkbox
            onChange={field.handleChange}
            checked={field.value}
            value="true"
            color="secondary"
          />
        }
        label={label}
      />
      {form.errors[field.name] && form.touched[field.name] && (
        <span style={{ ...inputErrorStyle, margin: "-8px 0 4px" }}>
          {form.errors[field.name]}
        </span>
      )}
    </>
  );
};

// Checkbox - Group (for FieldArray)
//
const FormCheckBox = ({ cbprops, label, ...rest }: any) => {
  const { classes } = rest;
  const HtmlToReactParser = require("html-to-react").Parser;
  const parser = new HtmlToReactParser();
  return (
    <FormControlLabel
      classes={{ root: classes.root, label: classes.label }}
      control={<Checkbox {...cbprops} />}
      label={parser.parse(label)}
    />
  );
};

const FormCheckBoxForFieldArray = (large: boolean) => {
  const style = large ? labelStyleLarge : labelStyleNormal;
  return withStyles(createStyles(style as any))(FormCheckBox);
};

type CheckBoxData = {
  id: string; // unused in checkboxListForBool
  name: string;
};

type CheckBoxHelperParams = {
  dataList: CheckBoxData[];
  valueSet: string[];
  valueName: string;
  single?: boolean;
  errors?: any;
  touched?: any;
  disabled?: any;
};

// Render a list of checkbox for add/remove data in an array (e.g. Profile)
export const checkboxListForArray = (params: CheckBoxHelperParams) => {
  const { dataList, valueSet, valueName, errors } = params;

  const TagName = FormCheckBoxForFieldArray(false);
  return (arrayHelpers: any) => (
    <div style={{ margin: "0 auto", padding: "0 0.6rem" }}>
      {dataList.map((data) => {
        const cbprops = {
          // props for <CheckBox />
          name: valueName,
          value: data.id,
          checked: valueSet.includes(data.id),
          onChange: (e: any) => {
            if (e.target.checked) {
              arrayHelpers.push(data.id);
            } else {
              arrayHelpers.remove(valueSet.indexOf(data.id));
            }
          },
          color: "secondary",
        };
        return (
          <div key={data.id}>
            {<TagName cbprops={cbprops} label={data.name} />}
          </div>
        );
      })}
      {errors[valueName] && (
        <span style={inputErrorStyle}>&bull;&nbsp;{errors[valueName]}</span>
      )}
    </div>
  );
};

// Render a list of checkbox for ON/OFF data (e.g. ExSel)
export const checkboxListForBool = (params: CheckBoxHelperParams) => {
  const { dataList, valueSet, valueName, disabled, single } = params;

  const TagName = FormCheckBoxForFieldArray(true);
  return (arrayHelpers: any) => (
    <div style={{ margin: "0 auto", padding: "0 0.6rem" }}>
      {dataList.map((data, index) => {
        const cbprops = {
          // props for <CheckBox />
          name: valueName,
          checked: valueSet[index],
          onChange: (e: any) => {
            if (single) {
              valueSet.forEach((s, i) => arrayHelpers.replace(i, false));
            }
            arrayHelpers.replace(index, e.target.checked);
          },
          color: "secondary",
          disabled,
        };
        return (
          <div key={index}>
            {<TagName cbprops={cbprops} label={data.name} />}
          </div>
        );
      })}
    </div>
  );
};

// for Radiobutton in Field
const FormRadioButton = ({ field, ...rest }: any) => {
  const { classes } = rest;
  return (
    <>
      <FormControlLabel
        classes={{ label: classes.label }}
        style={{ margin: "0", maxHeight: "2.5rem" }}
        {...field}
        control={
          <Radio
            id={`${field.name}-${rest.id}`}
            onChange={field.onChange}
            value={rest.id}
            checked={rest.id === field.value}
            color="secondary"
          />
        }
        label={rest.label}
      />
    </>
  );
};
export const FormRadioButtonField = withStyles(
  createStyles(labelStyleNormal as any),
)(FormRadioButton);

export function svgRadioCtrl(
  value: string,
  classes: any,
  label: string,
  icon1: string,
  icon2: string,
): React.ReactNode {
  const iconStyle = { width: "40px", height: "24px", marginLeft: "2px" };
  return (
    <FormControlLabel
      classes={{ label: classes.controlLabel }}
      value={value}
      control={
        <Radio
          checkedIcon={
            <img src={require(`assets/svg/${icon1}.svg`)} style={iconStyle} />
          }
          icon={
            <img src={require(`assets/svg/${icon2}.svg`)} style={iconStyle} />
          }
        />
      }
      label={label}
    />
  );
}

const ruleEmail = {
  email: string()
    .max(80)
    .matches(
      /^[A-Za-z0-9\.=-]+@[\w\.-]+\.[\w]{2,3}$/,
      "Valid email is required.",
    )
    .required(sEmailRequired),
};
const rulePassword = {
  password: string()
    .matches(
      /(?=^.{8,32}$)((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#\$%\^&\*]).*$/,
      sPWRule,
    )
    .required(sPWRequired),
};
const ruleName = {
  name: string().max(40, sMaxName).min(2, sMinName).required(sNameRequired),
};
const ruleCode = {
  code: string().required(sCodeRequired).max(32),
};
const ruleAgreeTerms = {
  agree: boolean().oneOf([true], sAgreRequired),
};

const ruleJob = {
  jobs: array().required(sJobsRequired),
};
const ruleHowFound = {
  howf: array().required(sHowfRequired),
};

export const loginValidation = object().shape({
  ...ruleEmail,
  ...rulePassword,
});

export const resetValidation = object().shape({
  ...ruleCode,
  newPass1: rulePassword.password,
  newPass2: rulePassword.password,
});

export const chgPWValidation = object().shape({
  oldPass: rulePassword.password,
  newPass1: rulePassword.password,
  newPass2: rulePassword.password,
});

export const nameValidation = object().shape({
  ...ruleName,
});

export const emailValidation = object().shape({
  ...ruleEmail,
});

export const passwordValidation = object().shape({
  ...rulePassword,
});

export const signupValidation = object().shape({
  ...ruleName,
  ...ruleEmail,
  newPass1: rulePassword.password,
  newPass2: rulePassword.password,
  ...ruleAgreeTerms,
});

export const codeValidation = object().shape({
  ...ruleCode,
});

export const welcomeValidation = object().shape({
  // ...ruleGift,
  ...ruleJob,
  ...ruleHowFound,
});

export const jobValidation = object().shape({
  ...ruleJob,
});

export const surveyValidation = object().shape({
  Q1: string().required(sRadioErr),
  Q2: string().required(sRadioErr),
  // Q3: string().required(sRadioErr),
  email: string()
    .max(80)
    .matches(
      /^[A-Za-z0-9\.=-]+@[\w\.-]+\.[\w]{2,3}$/,
      "Email address is invalid.",
    ),
});

export const closeButton = (classes: any, handleClose: () => void) => (
  <Button
    simple
    className={classes.modalCloseButton}
    key="close"
    onClick={handleClose}>
    <Close />
  </Button>
);

export const submitExButton = (label: string, disabled: boolean) => (
  <Button
    type="submit"
    color={"secondary"}
    disabled={disabled}
    style={{
      fontSize: "1.2rem",
      color: "white",
      textTransform: "none",
      float: "right",
      marginTop: "3rem",
    }}>
    {label}
  </Button>
);

export const btnFormSubmit = (params: {
  label: string;
  disabled: boolean;
  size?: string;
  color?: string;
  id?: string;
}) => {
  const { label, disabled, size, color, id } = params;
  return (
    <Button
      id={id}
      type="submit"
      size={size}
      color={color || "success"}
      round
      disabled={disabled}>
      {!disabled ? <ThumbUp /> : ""}
      {label}
    </Button>
  );
};

export const btnFormAction = (params: {
  label: string;
  handler: () => void;
  size?: string;
  id?: string;
}) => {
  const { label, handler, size, id } = params;
  return (
    <Button
      id={id}
      round
      size={size}
      color="success"
      style={{ marginLeft: "0.5rem" }}
      onClick={handler}>
      {label}
    </Button>
  );
};

export function loginSocialButtons(classes: any) {
  // const socialList: string[] = ["Twitter", "Google", "Facebook"];
  const socialList: string[] = ["Twitter", "Google"];
  return socialList.map((provider) => {
    const name = provider.toLowerCase();
    return (
      <Button
        key={name}
        justIcon
        round
        href={`/prx/auth/${name}`}
        color={name}
        className={classes.socialLineButton}>
        <i className={`fab fa-${name === "facebook" ? "facebook-f" : name}`} />
      </Button>
    );
  });
}

export function followSocialButtons() {
  const socialList: { name: string; link: string }[] = [
    { name: "twitter", link: "https://twitter.com/ApricadN" },
    {
      name: "facebook",
      link:
        "https://www.facebook.com/Apricad-Courses-of-Nursing-104911124821820",
    },
  ];
  return socialList.map(({ name, link }, idx) => {
    return (
      <Button
        style={{ margin: "0 2px" }}
        key={idx}
        justIcon
        round
        onClick={() => window.open(link)}
        color={name}>
        <i className={`fab fa-${name === "facebook" ? "facebook-f" : name}`} />
      </Button>
    );
  });
}

export function passwordEyeIcon(
  showPassword: boolean,
  handleClick: () => void,
  id?: string,
) {
  return (
    <InputAdornment position="end">
      <IconButton id={id} onClick={handleClick}>
        {showPassword ? <Visibility /> : <VisibilityOff />}
      </IconButton>
    </InputAdornment>
  );
}

export function renderProgress() {
  return (
    <div>
      <LinearProgress style={{ margin: "8rem auto 1rem" }} color="primary" />
      <LinearProgress
        style={{ margin: "0 auto 2rem auto" }}
        color="secondary"
      />
    </div>
  );
}

export function progressCircles() {
  return (
    <div
      style={{
        position: "absolute",
        top: "3rem",
        left: "48%",
        zIndex: 9000,
      }}>
      <Fab color="secondary">
        <HourglassEmptyOutlined />
      </Fab>
      <CircularProgress
        size={68}
        style={{
          position: "absolute",
          top: -6,
          left: -6,
        }}
      />
    </div>
  );
}

export function backButton(history: any) {
  const style: any = {
    position: "absolute",
    "@media (minWidth: 1280px)": {
      right: "12vw",
    },
    right: "5vw",
    top: "80px",
    zIndex: 1,
  };

  return (
    <div style={style}>
      <Tooltip title="Back">
        <Fab size="medium" color="secondary" onClick={history.goBack}>
          <Undo />
        </Fab>
      </Tooltip>
    </div>
  );
}
